import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../application/hooks/useTranslation';
import type { CommissionSummaryDto } from '../../../domain/contracts/api.contracts';
import { resolveWallet } from '../../../domain/calculators/processWithdrawal';
import { MIN_WITHDRAWAL_BDT, withdrawBlocked } from '../../../domain/calculators/commissions';
import { formatBDT } from '../../../domain/formatters/currency';
import { formatNumber } from '../../../domain/formatters/date';
import { AmountDisplay } from '../ui/AmountDisplay';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';

export interface BalanceHeroProps {
  summary: CommissionSummaryDto;
  /** Withdrawal requests not yet paid out or closed (from the withdrawal history). */
  requestsInProgress: number;
}

/**
 * Available balance + Withdraw CTA. While the balance is under the minimum, the reason and
 * progress are visible text wired to the button via aria-describedby (never a `title` only).
 * Smaller stats sit beneath: total earned, requests in progress, paid out, team members.
 */
export function BalanceHero({ summary, requestsInProgress }: BalanceHeroProps) {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const wallet = resolveWallet(summary);
  const available = wallet.availableForWithdrawal;
  const blocked = withdrawBlocked(available);
  const safe = Number.isFinite(available) ? Math.max(0, available) : 0;
  const pct = Math.min(100, Math.round((safe / MIN_WITHDRAWAL_BDT) * 100));
  const min = formatBDT(MIN_WITHDRAWAL_BDT, locale);

  return (
    <>
      <Card className="balance-hero" data-testid="available-balance">
        <div className="stat-label">{t('commissions.available')}</div>
        <div className="hero-amount">
          <AmountDisplay amount={available} bold />
        </div>
        {blocked && (
          <div id="withdraw-reason" className="hero-reason" data-testid="withdraw-reason">
            <div
              className="progress-track"
              role="progressbar"
              aria-label={t('commissions.minProgress', { min })}
              aria-valuemin={0}
              aria-valuemax={MIN_WITHDRAWAL_BDT}
              aria-valuenow={Math.min(safe, MIN_WITHDRAWAL_BDT)}
            >
              <div className="progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <p className="muted">
              {t('commissions.withdrawNeeded', { amount: formatBDT(MIN_WITHDRAWAL_BDT - safe, locale), min })}
            </p>
          </div>
        )}
        <Button
          className="hero-cta"
          disabled={blocked}
          aria-describedby={blocked ? 'withdraw-reason' : undefined}
          onClick={() => navigate('/withdrawals')}
          data-testid="withdraw-cta"
        >
          {t('commissions.withdraw')}
        </Button>
        {requestsInProgress > 0 && (
          <Link to="/withdrawals" className="chip chip-link tap" data-testid="requests-chip">
            {t(requestsInProgress === 1 ? 'commissions.requestOne' : 'commissions.requestMany', {
              count: formatNumber(requestsInProgress, locale),
            })}{' '}
            →
          </Link>
        )}
      </Card>
      <div className="stat-grid">
        {wallet.processingSupported && (
          <Card data-testid="available-commission-stat">
            <div className="stat-label">{t('dashboard.availableCommission')}</div>
            <div className="stat-value"><AmountDisplay amount={wallet.availableCommission} /></div>
          </Card>
        )}
        <Card>
          <div className="stat-label">{t('commissions.totalEarned')}</div>
          <div className="stat-value"><AmountDisplay amount={summary.totalEarned} /></div>
        </Card>
        <Card>
          <div className="stat-label">{t('commissions.inProgress')}</div>
          <div className="stat-value"><AmountDisplay amount={summary.pendingWithdrawals} /></div>
        </Card>
        <Card>
          <div className="stat-label">{t('commissions.withdrawn')}</div>
          <div className="stat-value"><AmountDisplay amount={summary.totalWithdrawn} /></div>
        </Card>
        <Card>
          <div className="stat-label">{t('commissions.team')}</div>
          <div className="stat-value tabular">
            {summary.downlineCount == null ? '—' : formatNumber(summary.downlineCount, locale)}
          </div>
        </Card>
      </div>
    </>
  );
}
